import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { FiUsers, FiUserCheck, FiUserX, FiSlash } from "react-icons/fi";
import jsPDF from "jspdf";
import Sidebar from "../../components/Sidebar/Sidebar";
import Header from "../../components/Header/Header";
import { useConfirm } from "../../components/ConfirmDialog/ConfirmDialog";
import { getStaff, deleteStaff } from "../../services/staff_service";
import { getCurrentUser } from "../../services/auth_service";
import {
  isSuperadmin,
  hasPermission,
  getPermissionLevel,
} from "../utils/permissions";
import "./StaffManagement.css";
const API_BASE_URL = "https://srinivasafleet.com/api";

const resolvePhotoUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;
  return `${API_BASE_URL}${path}`;
};
function getInitials(name = "") {
  return name.split(" ").slice(0, 2).map(w => w[0]?.toUpperCase() ?? "").join("");
}
const AVATAR_COLORS = ["av-blue","av-green","av-amber","av-purple","av-red","av-teal","av-pink"];
function avatarColor(id) { return AVATAR_COLORS[id % AVATAR_COLORS.length]; }

function formatDate(dateStr) {
  return dateStr || "—";
}

function StatCard({ label, value, variant, icon }) {
  return (
    <div className={`st-stat-card${variant ? ` st-stat-card--${variant}` : ""}`}>
      <div className="st-stat-icon">{icon}</div>
      <div className="st-stat-text">
        <span className={`st-stat-value ${variant ?? ""}`}>{value}</span>
        <span className="st-stat-label">{label}</span>
      </div>
    </div>
  );
}

function StatusBadge({ status }) {
  const map = {
    "Active":       "st-badge-success",
    "Deactive":     "st-badge-danger",
    "Block Listed": "st-badge-warning",
  };
  return (
    <span className={`st-badge ${map[status] ?? "st-badge-muted"}`}>
      <span className="st-badge-dot" />{status || "—"}
    </span>
  );
}

function RolePill({ children }) { return <span className="st-role-pill">{children}</span>; }

function ActionsDropdown({ onView, onEdit, onDelete, canView, canEdit, canDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function onClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const itemCount =
    Number(Boolean(canView)) +
    Number(Boolean(canEdit)) +
    Number(Boolean(canDelete));

  if (itemCount === 0) return null;

  return (
    <div className="st-actions-dropdown" ref={ref}>
      <button
        className={`st-actions-trigger${open ? " st-actions-trigger--open" : ""}`}
        onClick={() => setOpen(v => !v)}
      >
        Actions
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div className="st-actions-menu">
          {canView && (
            <button
              className="st-actions-menu-item"
              onClick={() => { setOpen(false); onView(); }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8Z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              View
            </button>
          )}

          {canEdit && (
            <>
              {canView && <div className="st-actions-menu-divider" />}
              <button
                className="st-actions-menu-item st-actions-menu-item--edit"
                onClick={() => { setOpen(false); onEdit(); }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                  <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1-1-4Z" />
                </svg>
                Edit
              </button>
            </>
          )}

          {canDelete && (
            <>
              {(canView || canEdit) && <div className="st-actions-menu-divider" />}
              <button
                className="st-actions-menu-item st-actions-menu-item--delete"
                onClick={() => { setOpen(false); onDelete(); }}
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6M14 11v6" />
                  <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
                </svg>
                Delete
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function FilterDropdown({ staffs, filters, onChange, onClose }) {
  const roles = [...new Set(staffs.map(s => s.role_name).filter(Boolean))];
  const states = [...new Set(staffs.map(s => s.service_state).filter(Boolean))];
  const financialYears = [...new Set(staffs.map(s => s.financial_year_label).filter(Boolean))];
  const STATUSES = ["Active", "Deactive", "Block Listed"];

  return (
    <div className="st-filter-dropdown">

      <div className="st-filter-section">
        <div className="st-filter-label">Status</div>
        {STATUSES.map(s => (
          <label key={s} className="st-filter-option">
            <input type="checkbox"
              checked={filters.statuses?.includes(s) ?? false}
              onChange={e => {
                const cur = filters.statuses ?? [];
                const next = e.target.checked ? [...cur, s] : cur.filter(x => x !== s);
                onChange({ ...filters, statuses: next.length ? next : null });
              }}
            />
            {s}
          </label>
        ))}
      </div>

      {roles.length > 0 && (
        <div className="st-filter-section">
          <div className="st-filter-label">Role</div>
          {roles.map(role => (
            <label key={role} className="st-filter-option">
              <input type="checkbox"
                checked={filters.roles?.includes(role) ?? false}
                onChange={e => {
                  const cur = filters.roles ?? [];
                  const next = e.target.checked ? [...cur, role] : cur.filter(r => r !== role);
                  onChange({ ...filters, roles: next.length ? next : null });
                }}
              />
              {role}
            </label>
          ))}
        </div>
      )}

      {states.length > 0 && (
        <div className="st-filter-section">
          <div className="st-filter-label">Service State</div>
          {states.map(st => (
            <label key={st} className="st-filter-option">
              <input type="checkbox"
                checked={filters.states?.includes(st) ?? false}
                onChange={e => {
                  const cur = filters.states ?? [];
                  const next = e.target.checked ? [...cur, st] : cur.filter(x => x !== st);
                  onChange({ ...filters, states: next.length ? next : null });
                }}
              />
              {st}
            </label>
          ))}
        </div>
      )}

      {financialYears.length > 0 && (
        <div className="st-filter-section">
          <div className="st-filter-label">Financial Year</div>
          {financialYears.map(fy => (
            <label key={fy} className="st-filter-option">
              <input type="checkbox"
                checked={filters.financialYears?.includes(fy) ?? false}
                onChange={e => {
                  const cur = filters.financialYears ?? [];
                  const next = e.target.checked ? [...cur, fy] : cur.filter(x => x !== fy);
                  onChange({ ...filters, financialYears: next.length ? next : null });
                }}
              />
              {fy}
            </label>
          ))}
        </div>
      )}

      <div className="st-filter-actions">
        <button className="st-btn st-btn-ghost st-btn-sm"
          onClick={() => onChange({ statuses: null, verified: null, roles: null, states: null, financialYears: null })}>
          Clear all
        </button>
        <button className="st-btn st-btn-add st-btn-sm" onClick={onClose}>Apply</button>
      </div>
    </div>
  );
}
function PhotoModal({ photoUrl, name, onClose }) {
  return (
    <div className="st-staff-photo-modal-overlay" onClick={onClose}>
      <div className="st-staff-photo-modal" onClick={(e) => e.stopPropagation()}>
        <button className="st-staff-photo-modal-close" onClick={onClose}>×</button>
        <img src={photoUrl} alt={name} className="st-staff-photo-modal-img" />
        <p className="st-staff-photo-modal-name">{name}</p>
      </div>
    </div>
  );
}
function exportToPDF(staffList) {
  const style = `
    <style>
      body { font-family: Arial, sans-serif; font-size: 12px; color: #111; margin: 24px; }
      h1 { font-size: 18px; margin-bottom: 4px; }
      p { margin: 0 0 16px; color: #555; font-size: 11px; }
      table { width: 100%; border-collapse: collapse; }
      th { background: #f3f4f6; text-align: left; padding: 8px 10px; font-size: 11px;
           text-transform: uppercase; letter-spacing: .05em; color: #6b7280;
           border-bottom: 1px solid #e5e7eb; }
      td { padding: 8px 10px; border-bottom: 1px solid #f3f4f6; vertical-align: top; }
      tr:last-child td { border-bottom: none; }
      .badge { display: inline-block; padding: 2px 8px; border-radius: 99px;
               font-size: 10px; font-weight: 600; }
      .green  { background:#dcfce7; color:#166534; }
      .red    { background:#fee2e2; color:#991b1b; }
      .amber  { background:#fef9c3; color:#854d0e; }
      .grey   { background:#f3f4f6; color:#374151; }
    </style>`;

  const statusClass = s =>
    s === "Active" ? "green" : s === "Block Listed" ? "amber" : "red";

  const rows = staffList.map(s => `
    <tr>
      <td>${s.full_name ?? "—"}</td>
      <td>${s.staff_code ?? "—"}</td>
      <td>${s.designation ?? "—"}</td>
      <td>${s.email ?? "—"}</td>
      <td>${s.mobile ?? "—"}</td>
      <td>${s.role_name ?? "—"}</td>
      <td>${s.service_state ?? "—"}</td>
      <td>${s.branch_name ?? "—"}</td>
      <td><span class="badge ${statusClass(s.status)}">${s.status ?? "—"}</span></td>
    </tr>`).join("");

  const html = `<!DOCTYPE html><html><head><meta charset="utf-8">${style}</head>
<body>
  <h1>Staff Report</h1>
  <p>Exported on ${new Date().toLocaleString()} · ${staffList.length} member${staffList.length !== 1 ? "s" : ""}</p>
  <table>
    <thead><tr>
      <th>Name</th><th>Staff Code</th><th>Designation</th><th>Email</th>
      <th>Mobile</th><th>Role</th><th>State</th><th>Branch</th>
      <th>Status</th>
    </tr></thead>
    <tbody>${rows}</tbody>
  </table>
</body></html>`;

  const win = window.open("", "_blank");
  if (!win) { alert("Allow pop-ups to export PDF."); return; }
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
}
async function printStaffProfile(staff, resolvedUrl) {
  let photoDataUrl = null;
  if (resolvedUrl) {
    photoDataUrl = await getImageAsBase64(resolvedUrl);
  }

  const escapeHtml = (value) =>
    String(value ?? "—")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");

  const valueText = (value) => {
    if (value === null || value === undefined || value === "") return "—";
    return String(value);
  };

  const statusClass =
    staff.status === "Active" ? "status-active" :
    staff.status === "Block Listed" ? "status-warning" :
    "status-danger";

  const serviceState = staff.service_location_name || staff.service_state || "—";
  const fullAddress = [staff.address, staff.city, staff.pincode].filter(Boolean).join(", ") || "—";
  const initials = escapeHtml(getInitials(staff.full_name));

  const field = (label, value, full = false) => `
    <div class="info-card${full ? " full" : ""}">
      <div class="info-label">${escapeHtml(label)}</div>
      <div class="info-value">${escapeHtml(valueText(value))}</div>
    </div>`;

  const avatarHtml = photoDataUrl
    ? `<img class="profile-photo" src="${photoDataUrl}" alt="${escapeHtml(staff.full_name)}">`
    : `<div class="profile-photo fallback">${initials}</div>`;

  const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${escapeHtml(staff.full_name || "Staff Profile")}</title>
<style>
  @page { size: A4 portrait; margin: 0; }

  * {
    box-sizing: border-box;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  html, body {
    margin: 0;
    padding: 0;
    width: 100%;
    min-height: 100%;
    background: #eef2f7;
    font-family: Arial, Helvetica, sans-serif;
    color: #0f172a;
  }

  .print-page {
    width: 210mm;
    min-height: 297mm;
    margin: 0 auto;
    background: #fff;
    position: relative;
    overflow: hidden;
  }

  .top-accent {
    height: 4px;
    background: linear-gradient(90deg, #2563eb 0%, #2563eb 72%, #16a6b8 100%);
  }

  .profile-header {
    min-height: 102px;
    padding: 20px 30px;
    display: flex;
    align-items: center;
    gap: 16px;
    background: linear-gradient(112deg, #0a1f5c 0%, #1f56bd 62%, #18a6b8 100%);
    color: #fff;
  }

  .profile-photo {
    width: 64px;
    height: 64px;
    min-width: 64px;
    border-radius: 50%;
    object-fit: cover;
    display: block;
    border: 3px solid rgba(255,255,255,.95);
    box-shadow: 0 0 0 3px rgba(255,255,255,.16);
    background: #e2e8f0;
  }

  .profile-photo.fallback {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #0a1f5c;
    background: #e2e8f0;
    font-size: 19px;
    font-weight: 800;
  }

  .profile-main {
    min-width: 0;
    flex: 1;
  }

  .profile-name {
    margin: 0 0 5px;
    font-size: 21px;
    line-height: 1.08;
    font-weight: 800;
  }

  .profile-meta {
    margin: 0;
    color: #dbeafe;
    font-size: 10.5px;
    line-height: 1.5;
    font-weight: 500;
  }

  .profile-secondary {
    margin: 3px 0 0;
    color: #bfdbfe;
    font-size: 9px;
    line-height: 1.4;
  }

  .profile-status {
    flex: 0 0 auto;
    padding: 7px 13px;
    border-radius: 999px;
    font-size: 8.5px;
    font-weight: 800;
    letter-spacing: .04em;
    text-transform: uppercase;
  }

  .status-active { background: #dcfce7; color: #15803d; }
  .status-danger { background: #fee2e2; color: #b91c1c; }
  .status-warning { background: #fef3c7; color: #a16207; }

  .content {
    padding: 20px 30px 46px;
  }

  .section {
    margin: 0 0 15px;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .section-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 8px;
    color: #0f172a;
    font-size: 12px;
    font-weight: 800;
    letter-spacing: .04em;
    text-transform: uppercase;
  }

  .section-title::before {
    content: "";
    width: 4px;
    height: 18px;
    border-radius: 3px;
    background: #2563eb;
  }

  .grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
  }

  .info-card {
    min-height: 47px;
    padding: 8px 10px;
    background: #f7faff;
    border: 1px solid #d8e2ef;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    break-inside: avoid;
    page-break-inside: avoid;
  }

  .info-card.full {
    grid-column: 1 / -1;
  }

  .info-label {
    margin-bottom: 4px;
    color: #64748b;
    font-size: 7px;
    line-height: 1.1;
    font-weight: 800;
    letter-spacing: .06em;
    text-transform: uppercase;
  }

  .info-value {
    color: #0f172a;
    font-size: 10px;
    line-height: 1.25;
    font-weight: 700;
    overflow-wrap: anywhere;
  }

  .footer {
    position: absolute;
    left: 30px;
    right: 30px;
    bottom: 16px;
    padding-top: 7px;
    border-top: 1px solid #d8e2ef;
    display: flex;
    justify-content: space-between;
    color: #94a3b8;
    font-size: 7px;
  }

  @media print {
    html, body { background: #fff; }
    .print-page {
      margin: 0;
      width: 210mm;
      min-height: 297mm;
    }
  }
</style>
</head>
<body>
  <div class="print-page">
    <div class="top-accent"></div>

    <header class="profile-header">
      ${avatarHtml}
      <div class="profile-main">
        <h1 class="profile-name">${escapeHtml(valueText(staff.full_name))}</h1>
        <p class="profile-meta">${escapeHtml(valueText(staff.designation))} &nbsp;·&nbsp; ${escapeHtml(valueText(staff.role_name))}</p>
        <p class="profile-secondary">Staff Code: ${escapeHtml(valueText(staff.staff_code))}</p>
      </div>
      <div class="profile-status ${statusClass}">${escapeHtml(valueText(staff.status))}</div>
    </header>

    <main class="content">
      <section class="section">
        <h2 class="section-title">Contact</h2>
        <div class="grid">
          ${field("Email", staff.email)}
          ${field("Mobile", staff.mobile)}
          ${field("Address", fullAddress, true)}
          ${field("City", staff.city)}
          ${field("Pincode", staff.pincode)}
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">Employment</h2>
        <div class="grid">
          ${field("Staff Code", staff.staff_code)}
          ${field("Role", staff.role_name)}
          ${field("Service State", serviceState)}
          ${field("Branch", staff.branch_name)}
          ${field("Financial Year", staff.financial_year_label)}
          ${field("Status", staff.status)}
        </div>
      </section>

      <section class="section">
        <h2 class="section-title">Account</h2>
        <div class="grid">
          ${field("Staff ID", staff.id)}
          ${field("Active", staff.is_active ? "Yes" : "No")}
          ${field("Blocked", staff.is_blocked ? "Yes" : "No")}
          ${field("Mobile Verified", staff.mobile_verified ? "Yes" : "No")}
          ${field("Created By", staff.created_by_name)}
          ${field("Created On", formatDate(staff.created_at))}
          ${field("Updated By", staff.updated_by_name)}
          ${field("Last Updated", formatDate(staff.updated_at))}
        </div>
      </section>
    </main>

    <footer class="footer">
      <span>Srinivasa Fleet Management Pro</span>
      <span>Generated ${escapeHtml(new Date().toLocaleString())}</span>
    </footer>
  </div>

  <script>
    (() => {
      const printNow = () => {
        window.focus();
        window.print();
      };
      const images = Array.from(document.images);
      if (!images.length) {
        setTimeout(printNow, 150);
        return;
      }
      Promise.all(images.map(img => new Promise(resolve => {
        if (img.complete) {
          resolve();
          return;
        }
        img.onload = resolve;
        img.onerror = resolve;
      }))).then(() => setTimeout(printNow, 200));
    })();
  </script>
</body>
</html>`;

  const win = window.open("", "_blank", "width=1100,height=850");
  if (!win) {
    alert("Please allow pop-ups for this site to print the staff profile.");
    return;
  }

  win.document.open();
  win.document.write(html);
  win.document.close();
  win.focus();
}

async function getImageAsBase64(url) {
  try {
    const response = await fetch(url, { mode: "cors", cache: "no-store" });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const blob = await response.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch (err) {
    console.warn("Photo could not be embedded in PDF:", err.message);
    return null;
  }
}

function getPdfImageFormat(dataUrl) {
  if (!dataUrl) return "JPEG";
  if (dataUrl.startsWith("data:image/png")) return "PNG";
  if (dataUrl.startsWith("data:image/webp")) return "WEBP";
  return "JPEG";
}

async function cropPdfImageToCircle(dataUrl, size = 180) {
  if (!dataUrl) return null;

  return new Promise((resolve) => {
    const img = new Image();

    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = size;
        canvas.height = size;

        const ctx = canvas.getContext("2d");
        ctx.clearRect(0, 0, size, size);
        ctx.save();
        ctx.beginPath();
        ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2);
        ctx.closePath();
        ctx.clip();

        const scale = Math.max(size / img.naturalWidth, size / img.naturalHeight);
        const width = img.naturalWidth * scale;
        const height = img.naturalHeight * scale;

        ctx.drawImage(
          img,
          (size - width) / 2,
          (size - height) / 2,
          width,
          height
        );

        ctx.restore();
        resolve(canvas.toDataURL("image/png"));
      } catch {
        resolve(dataUrl);
      }
    };

    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

async function downloadStaffProfilePDF(staff, resolvedUrl) {
  const doc = new jsPDF({ unit: "pt", format: "a4", compress: true });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const margin = 34;
  const contentWidth = pageWidth - margin * 2;
  const footerY = pageHeight - 25;

  const colors = {
    navy: [10, 31, 92],
    blue: [37, 99, 235],
    cyan: [22, 166, 184],
    text: [15, 23, 42],
    muted: [100, 116, 139],
    border: [216, 226, 239],
    card: [247, 250, 255],
    white: [255, 255, 255],
    green: [21, 128, 61],
    greenBg: [220, 252, 231],
    red: [185, 28, 28],
    redBg: [254, 226, 226],
    amber: [161, 98, 7],
    amberBg: [254, 243, 199],
  };

  const valueText = (value) => {
    if (value === null || value === undefined || value === "") return "-";
    return String(value);
  };

  const serviceState = staff.service_location_name || staff.service_state || "-";
  const fullAddress = [staff.address, staff.city, staff.pincode].filter(Boolean).join(", ") || "-";

  let imgData = null;
  let circleData = null;

  if (resolvedUrl) {
    imgData = await getImageAsBase64(resolvedUrl);
    if (imgData) {
      circleData = await cropPdfImageToCircle(imgData, 180);
    }
  }

  let pageNumber = 1;
  let y = 0;

  const drawFooter = () => {
    doc.setDrawColor(...colors.border);
    doc.setLineWidth(0.5);
    doc.line(margin, footerY - 9, pageWidth - margin, footerY - 9);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(...colors.muted);
    doc.text("Srinivasa Fleet Management Pro", margin, footerY + 4);
    doc.text(`Page ${pageNumber}`, pageWidth - margin, footerY + 4, { align: "right" });
  };

  const addPage = () => {
    drawFooter();
    doc.addPage();
    pageNumber += 1;
    y = 34;
  };

  const ensureSpace = (height) => {
    if (y + height > footerY - 14) addPage();
  };

  const sectionTitle = (title) => {
    ensureSpace(25);

    doc.setFillColor(...colors.blue);
    doc.roundedRect(margin, y + 1, 3.5, 15, 1.5, 1.5, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...colors.text);
    doc.text(title.toUpperCase(), margin + 10, y + 12);

    y += 22;
  };

  const addFields = (fields) => {
    const gap = 6;
    const columns = 2;
    const cardWidth = (contentWidth - gap) / columns;
    const padding = 8;
    const labelSize = 6.6;
    const valueSize = 9.2;
    const lineHeight = 10;

    for (let i = 0; i < fields.length; i += columns) {
      const rowFields = [fields[i] || null, fields[i + 1] || null];
      let maxLines = 1;

      rowFields.forEach((itemField) => {
        if (!itemField) return;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(valueSize);
        const wrapped = doc.splitTextToSize(valueText(itemField[1]), cardWidth - padding * 2);
        maxLines = Math.max(maxLines, wrapped.length);
      });

      const cardHeight = Math.max(35, 27 + (maxLines - 1) * lineHeight);
      ensureSpace(cardHeight + gap);

      rowFields.forEach((itemField, columnIndex) => {
        if (!itemField) return;

        const x = margin + columnIndex * (cardWidth + gap);

        doc.setFillColor(...colors.card);
        doc.setDrawColor(...colors.border);
        doc.setLineWidth(0.45);
        doc.roundedRect(x, y, cardWidth, cardHeight, 5, 5, "FD");

        doc.setFont("helvetica", "bold");
        doc.setFontSize(labelSize);
        doc.setTextColor(...colors.muted);
        doc.text(String(itemField[0]).toUpperCase(), x + padding, y + 10);

        doc.setFont("helvetica", "bold");
        doc.setFontSize(valueSize);
        doc.setTextColor(...colors.text);

        const wrapped = doc.splitTextToSize(valueText(itemField[1]), cardWidth - padding * 2);
        doc.text(wrapped, x + padding, y + 16, { baseline: "top" });
      });

      y += cardHeight + gap;
    }

    y += 1;
  };

  const headerHeight = 104;

  doc.setFillColor(...colors.navy);
  doc.rect(0, 0, pageWidth, headerHeight, "F");

  doc.setFillColor(...colors.blue);
  doc.rect(0, 0, pageWidth * 0.72, 5, "F");

  doc.setFillColor(...colors.cyan);
  doc.rect(pageWidth * 0.72, 0, pageWidth * 0.28, 5, "F");

  const avatarSize = 58;
  const avatarX = margin + 7;
  const avatarY = 23;

  doc.setFillColor(...colors.white);
  doc.circle(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2 + 3, "F");

  if (circleData) {
    try {
      doc.addImage(circleData, "PNG", avatarX, avatarY, avatarSize, avatarSize);
    } catch {
      circleData = null;
    }
  }

  if (!circleData) {
    doc.setFillColor(226, 232, 240);
    doc.circle(avatarX + avatarSize / 2, avatarY + avatarSize / 2, avatarSize / 2, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(17);
    doc.setTextColor(...colors.navy);
    doc.text(
      getInitials(staff.full_name || ""),
      avatarX + avatarSize / 2,
      avatarY + avatarSize / 2 + 6,
      { align: "center" }
    );
  }

  const textX = avatarX + avatarSize + 17;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(...colors.white);
  doc.text(valueText(staff.full_name), textX, 40);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(219, 234, 254);
  doc.text(`${valueText(staff.designation)}   |   ${valueText(staff.role_name)}`, textX, 59);

  doc.setFontSize(8.5);
  doc.setTextColor(191, 219, 254);
  doc.text(
    `Staff Code: ${valueText(staff.staff_code)}   |   Financial Year: ${valueText(staff.financial_year_label)}`,
    textX,
    76
  );

  const status = valueText(staff.status).toUpperCase();
  const statusIsActive = staff.status === "Active";
  const statusIsWarning = staff.status === "Block Listed";

  const statusBg = statusIsActive
    ? colors.greenBg
    : statusIsWarning
      ? colors.amberBg
      : colors.redBg;

  const statusColor = statusIsActive
    ? colors.green
    : statusIsWarning
      ? colors.amber
      : colors.red;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  const statusW = doc.getTextWidth(status) + 20;
  const statusX = pageWidth - margin - statusW;

  doc.setFillColor(...statusBg);
  doc.roundedRect(statusX, 38, statusW, 22, 11, 11, "F");
  doc.setTextColor(...statusColor);
  doc.text(status, statusX + statusW / 2, 52, { align: "center" });

  y = headerHeight + 20;

  sectionTitle("Contact");
  addFields([
    ["Email", staff.email],
    ["Mobile", staff.mobile],
    ["Address", fullAddress],
    ["City", staff.city],
    ["Pincode", staff.pincode],
  ]);

  sectionTitle("Employment");
  addFields([
    ["Staff Code", staff.staff_code],
    ["Role", staff.role_name],
    ["Service State", serviceState],
    ["Branch", staff.branch_name],
    ["Financial Year", staff.financial_year_label],
    ["Status", staff.status],
  ]);

  sectionTitle("Account");
  addFields([
    ["Staff ID", staff.id],
    ["Active", staff.is_active ? "Yes" : "No"],
    ["Blocked", staff.is_blocked ? "Yes" : "No"],
    ["Mobile Verified", staff.mobile_verified ? "Yes" : "No"],
    ["Created By", staff.created_by_name],
    ["Created On", formatDate(staff.created_at)],
    ["Updated By", staff.updated_by_name],
    ["Last Updated", formatDate(staff.updated_at)],
  ]);

  drawFooter();

  const safeName = (staff.full_name || "staff")
    .trim()
    .replace(/\s+/g, "_")
    .replace(/[^\w-]/g, "");

  doc.save(`${safeName || "staff"}_profile.pdf`);
}

function DetailRow({ label, value, full }) {
  return (
    <div className={`st-view-row${full ? " st-view-row-full" : ""}`}>
      <span className="st-view-label">{label}</span>
      <span className="st-view-value">{value || "—"}</span>
    </div>
  );
}

function StaffViewModal({ staff, resolvedUrl, onClose }) {
  const [downloading, setDownloading] = useState(false);
  const fullAddress = [staff.address, staff.city, staff.pincode].filter(Boolean).join(", ");

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadStaffProfilePDF(staff, resolvedUrl);
    } catch (err) {
      console.error("PDF download failed:", err);
      alert("Could not generate PDF. Check console for details.");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="st-staff-view-modal-overlay" onClick={onClose}>
      <div className="st-staff-view-modal" onClick={(e) => e.stopPropagation()}>
        <button className="st-staff-view-modal-close" onClick={onClose}>×</button>

        <div className="st-view-header">
          {resolvedUrl ? (
            <img src={resolvedUrl} alt={staff.full_name} className="st-view-avatar-img" />
          ) : (
            <div className={`st-avatar st-avatar-lg ${avatarColor(staff.id)}`}>
              {getInitials(staff.full_name)}
            </div>
          )}
          <div>
            <h2 className="st-view-name">{staff.full_name}</h2>
            <p className="st-view-subtitle">{staff.designation || "—"} · {staff.role_name || "—"}</p>
            <div className="st-view-badges">
              <StatusBadge status={staff.status} />
            </div>
          </div>
        </div>

        <div className="st-view-scroll">
          <div className="st-view-section">
            <div className="st-view-section-title">Contact</div>
            <div className="st-view-grid">
              <DetailRow label="Email" value={staff.email} />
              <DetailRow label="Mobile" value={staff.mobile} />
              <DetailRow label="Address" value={fullAddress} full />
              <DetailRow label="City" value={staff.city} />
              <DetailRow label="Pincode" value={staff.pincode} />
            </div>
          </div>

          <div className="st-view-section">
            <div className="st-view-section-title">Employment</div>
            <div className="st-view-grid">
              <DetailRow label="Staff Code" value={staff.staff_code} />
              <DetailRow label="Role" value={staff.role_name} />
              <DetailRow label="Service State" value={staff.service_location_name} />
              <DetailRow label="Branch" value={staff.branch_name} />
              <DetailRow label="Financial Year" value={staff.financial_year_label} />
              <DetailRow label="Status" value={staff.status} />
            </div>
          </div>

          <div className="st-view-section">
            <div className="st-view-section-title">Account</div>
            <div className="st-view-grid">
              <DetailRow label="Staff ID" value={staff.id} />
              <DetailRow label="Active" value={staff.is_active ? "Yes" : "No"} />
              <DetailRow label="Blocked" value={staff.is_blocked ? "Yes" : "No"} />
              <DetailRow label="Mobile Verified" value={staff.mobile_verified ? "Yes" : "No"} />
              <DetailRow label="Created By" value={staff.created_by_name} />
              <DetailRow label="Created On" value={formatDate(staff.created_at)} />
              <DetailRow label="Updated By" value={staff.updated_by_name} />
              <DetailRow label="Last Updated" value={formatDate(staff.updated_at)} />
            </div>
          </div>
        </div>

        <div className="st-view-actions">
          <button className="st-btn st-btn-ghost" onClick={() => printStaffProfile(staff, resolvedUrl)}>
            Print
          </button>
          <button className="st-btn st-btn-add" onClick={handleDownload} disabled={downloading}>
            {downloading ? "Preparing…" : "Download PDF"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function StaffManagement() {
  const navigate = useNavigate();
  const currentUser = getCurrentUser() || {};
  const isSuperadminUser = isSuperadmin();
  const isAdminUser =
    String(currentUser?.role_name || "").trim().toLowerCase() === "admin";

  const canViewStaff =
    isSuperadminUser || hasPermission("staff_management", "view");

  const canCreate =
    isSuperadminUser ||
    isAdminUser;

  const canEdit =
    isSuperadminUser || hasPermission("staff_management", "edit");

  const canDelete =
    isSuperadminUser || hasPermission("staff_management", "delete");

  void currentUser;
  const confirm = useConfirm();
  const [staffs, setStaffs]         = useState([]);
  const [search, setSearch]         = useState("");
  const [loading, setLoading]       = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [filters, setFilters]       = useState({
    statuses: null, verified: null, roles: null, states: null, financialYears: null,
  });
  const [photoModal, setPhotoModal] = useState(null); 
  const [viewStaff, setViewStaff]   = useState(null); 
  const PAGE_SIZE = 15;
  const [currentPage, setCurrentPage] = useState(1);
  const filterRef = useRef(null);
  useEffect(() => { fetchStaff(); }, []);
  useEffect(() => {
    function onClickOutside(e) {
      if (filterRef.current && !filterRef.current.contains(e.target)) setShowFilter(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  const fetchStaff = async () => {
    setLoading(true);
    try {
      const response = await getStaff();
      setStaffs(response);
    } catch (error) {
      console.error(error);
      alert("Unable to fetch staff");
    } finally {
      setLoading(false);
    }
  };

  const handleView = (item) => {
    setViewStaff(item);
  };

  const handleDelete = async (id) => {
    if (!canDelete) return;

    const ok = await confirm({
      title: "Delete Staff",
      message: "Are you sure you want to delete this staff member? This action cannot be undone.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      variant: "danger",
    });

    if (!ok) return;

    try {
      await deleteStaff(id);
      await fetchStaff();
    } catch (error) {
      console.error(error);
      alert("Unable to delete staff");
    }
  };

  const activeFilterCount = [
    filters.statuses?.length > 0,
    filters.roles?.length > 0,
    filters.states?.length > 0,
    filters.financialYears?.length > 0,
  ].filter(Boolean).length;

  const filteredStaff = staffs.filter(item => {
    const q = search.toLowerCase();
    const matchesSearch =
      item.full_name?.toLowerCase().includes(q)   ||
      item.email?.toLowerCase().includes(q)        ||
      item.mobile?.includes(q)                     ||
      item.staff_code?.toLowerCase().includes(q)   ||
      item.designation?.toLowerCase().includes(q);

    const matchesStatus   = !filters.statuses?.length   || filters.statuses.includes(item.status);
    const matchesRole     = !filters.roles?.length       || filters.roles.includes(item.role_name);
    const matchesState    = !filters.states?.length      || filters.states.includes(item.service_state);
    const matchesFY       = !filters.financialYears?.length || filters.financialYears.includes(item.financial_year_label);

    return matchesSearch && matchesStatus && matchesRole && matchesState && matchesFY;
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [staffs, search, filters]);

  const totalPages = Math.max(1, Math.ceil(filteredStaff.length / PAGE_SIZE));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginatedStaff = filteredStaff.slice(
    (safeCurrentPage - 1) * PAGE_SIZE,
    safeCurrentPage * PAGE_SIZE
  );

  if (!canViewStaff) {
    return (
      <div className="st-staff-layout">
        <Sidebar />
        <div className="st-staff-main">
          <Header />
          <div className="st-staff-page">
            <div className="st-empty-state">
              <p className="st-empty-title">Access denied</p>
              <p className="st-empty-body">You don't have permission to view Staff Management.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const totalActive      = staffs.filter(s => s.status === "Active").length;
  const totalInactive    = staffs.filter(s => s.status === "Deactive").length;
  const totalBlockListed = staffs.filter(s => s.status === "Block Listed").length;

  return (
    <div className="st-staff-layout">
      <Sidebar />
      <div className="st-staff-main">
        <Header />
        <div className="st-staff-page">

          <div className="st-stats-grid">
            <StatCard label="Total Staff"    value={staffs.length}     icon={<FiUsers />}      variant="primary" />
            <StatCard label="Active"         value={totalActive}       icon={<FiUserCheck />}  variant="green"   />
            <StatCard label="Deactive"       value={totalInactive}     icon={<FiUserX />}      variant="red"    />
            <StatCard label="Block Listed"   value={totalBlockListed}  icon={<FiSlash />}      variant="amber"  />
          </div>

          <div className="st-toolbar">
            <div className="st-search-wrap">
              <svg className="st-search-icon" width="15" height="15" viewBox="0 0 24 24"
                fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
              </svg>
              <input type="text" placeholder="Search by name, code, designation, mobile…"
                value={search} onChange={e => setSearch(e.target.value)} />
              {search && (
                <button className="st-search-clear" onClick={() => setSearch("")}>×</button>
              )}
            </div>

            <div className="st-toolbar-actions">
              <div className="st-filter-wrap" ref={filterRef}>
                <button className={`st-btn st-btn-ghost ${activeFilterCount > 0 ? "st-btn-ghost--active" : ""}`}
                  onClick={() => setShowFilter(v => !v)}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                  </svg>
                  Filter
                  {activeFilterCount > 0 && <span className="st-filter-badge">{activeFilterCount}</span>}
                </button>
                {showFilter && (
                  <FilterDropdown staffs={staffs} filters={filters}
                    onChange={setFilters} onClose={() => setShowFilter(false)} />
                )}
              </div>

              <button className="st-btn st-btn-ghost" onClick={() => exportToPDF(filteredStaff)} title="Export PDF">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                  <polyline points="7 10 12 15 17 10"/>
                  <line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                Export PDF
              </button>

              {canCreate && (
                <button className="st-btn st-btn-add" onClick={() => navigate("/staff/add")}>
                  <span className="st-btn-icon">+</span> Add Staff
                </button>
              )}
            </div>
          </div>

          <div className="st-table-card">
            {loading ? (
              <div className="st-empty-state">
                <div className="st-loading-spinner" />
                <p>Loading staff…</p>
              </div>
            ) : (
              <div className="st-table-scroll">
              <table>
                <colgroup>
                  <col style={{ width: "126px" }} />
                  <col style={{ width: "180px" }} />
                  <col style={{ width: "140px" }} />
                  <col style={{ width: "125px" }} />
                  <col style={{ width: "115px" }} />
                  <col style={{ width: "92px" }} />
                  <col style={{ width: "155px" }} />
                  <col style={{ width: "135px" }} />
                  <col style={{ width: "120px" }} />
                  <col style={{ width: "126px" }} />
                </colgroup>
                <thead>
                  <tr>
                    <th>Photo</th>
                    <th>Staff Member</th>
                    <th>Staff Code</th>
                    <th>Designation</th>
                    <th>Mobile</th>
                    <th>Role</th>
                    <th>Service Location</th>
                    <th>Branch</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedStaff.length > 0 ? (
                    paginatedStaff.map(item => {
                      const resolvedUrl = resolvePhotoUrl(item.photo_url);
                      return (
                        <tr
                          key={item.id}
                          className="st-staff-row-clickable"
                          onClick={() => setViewStaff(item)}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            {resolvedUrl ? (
                              <img
                                src={resolvedUrl}
                                alt={item.full_name}
                                className="st-staff-avatar-img st-staff-avatar-img--clickable"
                                title="Click to view full size"
                                onClick={() => setPhotoModal({ url: resolvedUrl, name: item.full_name })}
                                onError={(e) => { e.target.style.display = "none"; }}
                              />
                            ) : (
                              <div className={`st-avatar ${avatarColor(item.id)}`}>
                                {getInitials(item.full_name)}
                              </div>
                            )}
                          </td>
                          <td>
                            <div className="st-name-cell">
                              <div>
                                <div className="st-staff-name">{item.full_name}</div>
                                <div className="st-staff-email">{item.email || "—"}</div>
                              </div>
                            </div>
                          </td>
                          <td className="st-cell-muted">{item.staff_code || "—"}</td>
                          <td className="st-cell-muted">{item.designation || "—"}</td>
                          <td className="st-cell-muted">{item.mobile}</td>
                          <td><RolePill>{item.role_name}</RolePill></td>
                          <td className="st-cell-muted">{item.service_location_name || "—"}</td>
                          <td className="st-cell-muted">{item.branch_name || "—"}</td>
                          <td><StatusBadge status={item.status} /></td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <ActionsDropdown
                              onView={() => handleView(item)}
                              onEdit={() => navigate(`/staff/edit/${item.id}`)}
                              onDelete={() => handleDelete(item.id)}
                              canView={canViewStaff}
                              canEdit={canEdit}
                              canDelete={canDelete}
                            />
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="10">
                        <div className="st-empty-state">
                          <div className="st-empty-icon">🔍</div>
                          <p className="st-empty-title">No staff found</p>
                          <p className="st-empty-body">
                            {search || activeFilterCount > 0
                              ? "No results match your search or filters."
                              : "No staff members yet."}
                          </p>
                          {!search && activeFilterCount === 0 && canCreate && (
                            <button className="st-btn st-btn-add" onClick={() => navigate("/staff/add")}>
                              Add Staff
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            )}
          </div>

          {!loading && filteredStaff.length > 0 && (
            <div className="st-table-footer st-table-footer--paginated">
              <span className="st-footer-count">
                {(safeCurrentPage - 1) * PAGE_SIZE + 1}
                –{Math.min(safeCurrentPage * PAGE_SIZE, filteredStaff.length)} of {filteredStaff.length} staff members
              </span>

              {totalPages > 1 && (
                <div className="st-pagination-controls">
                  <button
                    type="button"
                    className="st-btn st-btn-ghost st-btn-sm"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={safeCurrentPage <= 1}
                  >
                    Prev
                  </button>
                  <span className="st-pagination-page-label">
                    {safeCurrentPage} / {totalPages}
                  </span>
                  <button
                    type="button"
                    className="st-btn st-btn-ghost st-btn-sm"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={safeCurrentPage >= totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}

        </div>
      </div>

      {photoModal && (
        <PhotoModal
          photoUrl={photoModal.url}
          name={photoModal.name}
          onClose={() => setPhotoModal(null)}
        />
      )}

      {viewStaff && (
        <StaffViewModal
          staff={viewStaff}
          resolvedUrl={resolvePhotoUrl(viewStaff.photo_url)}
          onClose={() => setViewStaff(null)}
        />
      )}
    </div>
  );
}